import React from 'react'
import { connect } from 'react-redux'
import { OmSpinner, omMessage } from '@owma/kit'

import i18n from 'i18n'
import classNames from 'classnames'

import { ACTION, TYPE } from 'pages/main/data/defConstants'
import { getMessageId } from 'pages/main/data/JMAPMailbox'
import { ServerRecoveryController } from '../serverRecoveryHelper'

import dataManager from 'pages/main/data/DataManager'
import emailHelper from '../emailHelper'

import syncAniGIF from 'assets/images/gif/loading.gif'

import EmailContainer from 'emailThreadViewer/EmailContainer'
import { resetMailView } from 'emailThreadViewer/helper/mailview'

import { initViewerApi, disposeViewerApi } from '../viewerExt'
import type { ViewerWindow } from '@owma/common-web/components/viewer'
import {
  attachReactMgr as attachReactMgrShared,
  detachReactMgr as detachReactMgrShared,
  extractInviteAddressList as extractInviteAddressListShared,
  handleCreateEvent as handleCreateEventShared,
  handleShowCalendar as handleShowCalendarShared,
  handleCopyInvitation as handleCopyInvitationShared,
  onCreateInvitation as onCreateInvitationShared,
  getJMAPMessageId as getJMAPMessageIdShared,
} from '../threadViewShared'
import type { ThreadViewApi } from '../threadViewApi'

import styles from '../style.module.scss'
import innerStyles from './style.module.scss'
import moment from 'moment'

import { OutlookAvailabilityTime } from 'pages/calendar/modules/outlookdatatime/OutlookAvailabilityTime'

import { modalEventEditor } from 'pages/calendar'

interface ThreadViewProps {
  innerRef?: any
  email?: any
  emails?: any[]
  curEmail?: any
  curMailbox?: any
  dateFormat?: string
  timeFormat?: string
  isUnknownAlwaysShowExternalResources?: boolean
  isKnownAlwaysShowExternalResources?: boolean
  user?: any
  runCommand?: (cmd: string, messages?: any[] | null, forced?: boolean, param2?: any) => void
  openSendMail?: (opts?: any) => void
  replyOrForwardEmail?: (cmd: string, email: any) => void
  deleteDraft?: (email: any) => void
  byViewer: boolean // viewer에서 시작된 동작으로 변경이 일어난 경우인지 확인.
}

interface ThreadViewState {
  showLoadingAni: boolean
  isLoading: boolean
  invitationMenuVisible: boolean
  invitationData: any
  // EmailContainer 마운트 트리거. componentDidMount 가 initViewerApi 로
  // host window 에 NineNative 를 먼저 등록한 뒤 setState 로 마운트 →
  // EmailContainer 의 callOnReady 가 NineNative 를 발견하도록 보장 (§3.3.1).
  innerMounted: boolean
  // §2c.2 — reload() 시 증가. EmailContainer key 로 사용되어 강제 remount.
  // host 페이지 location.reload() 를 회피 (R14).
  reloadKey: number
}

// inner mode 의 viewerWindow 는 host window 와 동일. viewerExt.tsx 의
// initViewerApi 호출 후 webObj / NineNative / initNative 가 host window 에
// 등록되므로 타입 좁힘만을 위한 alias interface.
interface ViewerLikeWindow extends ViewerWindow {
  initNative?: () => void
}

const mapStateToProps = ({ settings, user }: any) => ({
  isUnknownAlwaysShowExternalResources: settings.email.isUnknownAlwaysShowExternalResources,
  isKnownAlwaysShowExternalResources: settings.email.isKnownAlwaysShowExternalResources,
  dateFormat: settings.general?.dateFormat,
  timeFormat: settings.general?.timeFormat,
  user,
})

export class InnerViewer
  extends React.Component<ThreadViewProps, ThreadViewState>
  implements ThreadViewApi
{
  state: ThreadViewState = {
    showLoadingAni: false, // 최초 로딩 시 사용.
    isLoading: false, // 로딩 중인지 확인.
    invitationMenuVisible: false,
    invitationData: null,
    innerMounted: false,
    reloadKey: 0,
  }

  mounted = false
  loaded = false

  refObj: any = null
  // inner mode 에서는 viewer 가 host window 와 동일. iframe 없음.
  viewerWindow: ViewerLikeWindow = window as ViewerLikeWindow
  containerRef: HTMLDivElement | null = null
  observerObj: any = null
  emailInfoMap: any = {}
  searchTimer: any = null
  // M4 — showLoadingAni 의 setTimeout id 보관 (componentWillUnmount cleanup 용).
  loadingAniTimer: ReturnType<typeof setTimeout> | null = null

  // 서버 복구 시 본문 미로딩 메일 자동 재요청 (OFM-214). viewer window 는 host window.
  private serverRecoveryCtrl = new ServerRecoveryController({
    getMounted: () => this.mounted,
    getEmailInfoMap: () => this.emailInfoMap,
    getViewerWindow: () => this.viewerWindow,
  })

  public applyThreadFunc?: () => void = undefined
  public isFirstSet: boolean = false
  public isThreadMode: boolean = false
  public prevThreadInfo?: {
    emailIds: string[]
    curEmailId: string | null
    curThreadId: string | null
  } = undefined
  public loadingTimer: string | number | NodeJS.Timeout | null = null
  public loadingTimerRetureFunc: string | null = null
  public delayShowTimer: string | number | NodeJS.Timeout | null = null
  // initViewerApi가 양 뷰어 공통으로 기록하는 토큰(타입 호환용). InnerViewer는 host window라
  // about:blank 네비게이트가 없어 disposeViewerApi를 호출하지 않으므로 사용처는 없다.
  public ownInitNative?: () => void = undefined

  // OMD-181: 중첩 마운트(EML 미리보기)에서 host window.NineNative 를 덮어쓰기 직전 스냅샷하고,
  // 이 인스턴스가 등록한 값(소유 토큰)을 보관한다. 언마운트 시 {}/undefined 초기화가 아니라
  // 직전(호스트) 값으로 복원해, 미리보기 close 가 호스트의 viewer→native bridge 를 파괴하지
  // 않도록 한다(IFace 스냅샷/복원과 대칭). iframe 모드는 별도 contentWindow 라 무관.
  private prevNineNative?: any = undefined
  private ownNineNative?: any = undefined

  componentDidMount() {
    const { innerRef } = this.props

    ;(dataManager as any).addEmailChangeListener(this.onEmailChanged)

    this.refObj = this.createRefObj()

    if (innerRef != null) {
      if (typeof innerRef === 'function') {
        innerRef(this.refObj)
      } else {
        innerRef.current = this.refObj
      }
    }

    // inner mode 에서는 EmailContainer 가 자체적으로 i18n.on('languageChanged') 를
    // 등록 (EmailContainer/index.tsx:138) — host i18n 변경 시 EmailContainer 가 자동
    // react. InnerViewer 가 onLanguageChanged 핸들러를 등록하면 webObj.setLanguage 가
    // ViewerIFace.setLanguage → reduxStore.dispatch(settings/SET_LOCALE) → host saga
    // 의 i18n.changeLanguage → i18n languageChanged 이벤트 재발화 → 무한 루프 trigger.
    //
    // [미구현 — inner mode 미사용으로 보류] mailbox_name 재지역화:
    // mailbox_name/mailbox_fullname 은 host 가
    // emailHelper.createEmailInfoFromEmail 에서 mailbox.getDisplayName()/getFullName() 으로
    // 계산해 전달하는 "문자열" 이라, EmailContainer 가 languageChanged 로 자동 react 해도
    // 이 값들은 옛 언어로 고정된다(뷰어가 재지역화하지 못함). IFrameViewer 는
    // onLanguageChanged → applyEmailChanged 에서 emailInfo.generateData() 로 재생성해
    // notifyEmailChanged 로 다시 전달하지만, inner mode 는 위 무한 루프 때문에
    // onLanguageChanged 를 등록하지 않아 재전송 경로가 없다. inner mode 를 실제로 쓰게 되면,
    // 무한 루프를 피하는 별도 트리거(예: props 로 받은 locale 변화 감지)에서 host 가
    // 각 emailInfo 를 generateData() 후 webObj.notifyEmailChanged 로 재전송해야 한다.

    // inner mode 부트스트랩: viewerWindow === window 인 상태에서
    //   1) initViewerApi 가 window.initNative / window.NineNative 등록
    //   2) window.initNative() 호출하여 NineNative 객체 주입을 즉시 트리거
    //   3) setState({ innerMounted: true }) → EmailContainer 자식 마운트
    //
    // EmailContainer.componentDidMount 가 initIFace → mailview.callOnReady 를
    // 발화하기 전에 NineNative 가 host window 에 이미 등록되어 있어 §3.3.1 의
    // parentType silent break 를 회피 (R20).
    // OMD-181: 덮어쓰기 직전 host 의 NineNative 를 스냅샷(중첩 마운트 복원용).
    this.prevNineNative = this.viewerWindow.NineNative
    initViewerApi(this.viewerWindow, this)
    if ((window as any).initNative != null) {
      ;(window as any).initNative()
    }
    // initNative() 실행으로 이 인스턴스가 등록한 NineNative 를 소유 토큰으로 보관.
    this.ownNineNative = this.viewerWindow.NineNative
    this.mounted = true
    this.loaded = true
    this.setState({ innerMounted: true })

    // 서버 복구 시 viewer 에 표시 중이며 본문 미로딩인 메일의 body 를 자동 재요청한다.
    this.serverRecoveryCtrl.register()

    this.attachReactMgr(this.props)

    this.showLoadingAni(5000)
  }

  componentDidUpdate(prevProps: ThreadViewProps) {
    const { curEmail, email, emails, curMailbox, dateFormat, timeFormat } = this.props

    if (prevProps != null) {
      if (
        prevProps.email !== email ||
        prevProps.emails !== emails ||
        prevProps.curMailbox !== curMailbox
      ) {
        this.applyThread()

        this.detachReactMgr(prevProps)
        this.attachReactMgr(this.props)
      }
      if (
        (prevProps.curEmail == null && curEmail != null) ||
        (prevProps.curEmail != null && curEmail == null) ||
        (prevProps.curEmail != null &&
          curEmail != null &&
          prevProps.curEmail.data.id !== curEmail.data.id)
      ) {
        this.applyCurEmail()
      }
      if (prevProps.dateFormat !== dateFormat || prevProps.timeFormat !== timeFormat) {
        this.applyDateTimeFormat()
      }
    }
  }

  componentWillUnmount() {
    const { innerRef } = this.props

    if (innerRef != null) {
      if (typeof innerRef !== 'function') {
        innerRef.current = null
      }
    }

    ;(dataManager as any).removeEmailChangeListener(this.onEmailChanged)
    this.serverRecoveryCtrl.unregister()
    if (this.observerObj != null) {
      this.observerObj.clear()
    }

    // M4 — showLoadingAni 의 setTimeout 이 unmount 후 setState 호출하는 race
    // 회피. inner mode 는 iframe load delay 없이 빠르게 unmount 가능.
    if (this.loadingAniTimer != null) {
      clearTimeout(this.loadingAniTimer)
      this.loadingAniTimer = null
    }
    // M5 — notiCheck 재귀 setTimeout 의 guard. componentDidMount 에서 true 설정,
    // 여기서 false 설정 → 다음 iteration 가 silent return.
    this.mounted = false

    this.detachReactMgr(this.props)

    // InnerViewer 는 host window 에 initViewerApi 로 initNative/NineNative/threadViewInfo 를
    // 등록한다. iframe 모드와 달리 about:blank 로 폐기되는 contentWindow 가 없으므로,
    // 언마운트 시 직접 해제하지 않으면 host window 가 이 인스턴스(reactObj)를 영구 보존한다.
    // ownInitNative 토큰으로 자기 등록만 정확히 해제(다중 마운트 경쟁 안전).
    disposeViewerApi(this.ownInitNative)
    this.ownInitNative = undefined
    try {
      const viewerWindow = this.viewerWindow
      // OMD-181: 무조건 undefined 초기화가 아니라, 내가 등록한 값일 때만 직전(호스트)
      // NineNative 로 복원한다. 중첩 마운트(EML 미리보기) close 가 호스트의 viewer→native
      // bridge 를 파괴하지 않도록 하는 토큰 가드 + 스냅샷 복원.
      if (viewerWindow != null && viewerWindow.NineNative === this.ownNineNative) {
        viewerWindow.NineNative = this.prevNineNative
      }
    } catch (e) {
      console.warn('[InnerViewer] NineNative 해제 실패:', e)
    }
    this.ownNineNative = undefined
    this.prevNineNative = undefined
  }

  createRefObj = () => {
    const obj = {
      setFindText: (text: string) => {
        const viewerWindow = this.viewerWindow
        if (viewerWindow != null && viewerWindow.webObj != null) {
          viewerWindow.webObj.setFindText(text)
        }
      },
      printEmail: (email: any) => {
        // OMD-194: 인쇄는 host 가 오케스트레이션. NineNative.print 가 뷰어에서 payload 를
        // 되가져와(fold 처리 포함) 인쇄한다.
        const viewerWindow = this.viewerWindow
        if (viewerWindow != null && viewerWindow.NineNative != null && email != null) {
          viewerWindow.NineNative.print(email.getId())
        }
      },
      setHideEmptyIcon: (hideEmptyIcon: boolean) => {
        const viewerWindow = this.viewerWindow
        if (viewerWindow != null && viewerWindow.webObj != null) {
          viewerWindow.webObj.setHideEmptyIcon(hideEmptyIcon)
        }
      },
      // 에디터 탭 활성화 중 뷰어 bake(styleToInlineStyleAsync)를 일시정지해 메인 스레드 경합을
      // 없앤다. 'view' 탭 복귀 시 재개한다(host 의 setTabsActiveKey 가 토글). keep-alive 라
      // 뷰어는 상주하므로 webObj 가 안정적으로 존재한다.
      setBakePaused: (paused: boolean) => {
        const viewerWindow = this.viewerWindow
        if (viewerWindow != null && viewerWindow.webObj != null) {
          viewerWindow.webObj.setBakePaused(paused)
        }
      },
      // §2c.2 — inner mode 에서 viewerWindow === window 이므로 location.reload() 는
      // host 페이지 전체를 새로고침하는 잘못된 동작 (R14).
      // reloadKey state 를 bump 하여 EmailContainer 를 강제 remount 한다.
      //
      // mailview.ts 의 `mailViewInitialized` once-guard 가 reset 되지 않으면 새
      // EmailContainer 의 `initIFace → initMailView` 호출 시 `startMailView` 가
      // skip 되어 stale DocUtil 이 destroyed DOM 을 참조하는 회귀 발생
      // (code-reviewer CRITICAL-1). reloadKey bump 전에 resetMailView() 호출하여
      // 가드 reset → 다음 initMailView 가 새 DocUtil instance 를 생성.
      reload: () => {
        resetMailView()
        this.setState((prev) => ({ reloadKey: prev.reloadKey + 1 }))
      },
    }

    return obj
  }

  attachReactMgr = (props: ThreadViewProps): void => {
    attachReactMgrShared(props, this)
  }

  detachReactMgr = (props: ThreadViewProps): void => {
    detachReactMgrShared(props, this)
  }

  showLoadingAni = (delay = 200) => {
    this.setState({
      showLoadingAni: true,
    })
    if (delay > 0) {
      // M4 — timer id 보관해 componentWillUnmount 에서 clearTimeout. setState-on-unmounted 회피.
      if (this.loadingAniTimer != null) {
        clearTimeout(this.loadingAniTimer)
      }
      this.loadingAniTimer = setTimeout(() => {
        this.loadingAniTimer = null
        this.closeLoadingAni()
      }, delay)
    }
  }

  closeLoadingAni = () => {
    this.setState({
      showLoadingAni: false,
    })
  }

  setNormalLoading = (loading: boolean) => {
    this.setState({
      isLoading: loading,
    })
  }

  createEmailInfo = (email: any) => {
    const { curMailbox } = this.props

    const isOutbox = curMailbox != null && curMailbox.getRole() === 'outbox'

    const emailId = email.data.id
    this.emailInfoMap[emailId] = emailHelper.createEmailInfoFromEmail(
      email,
      isOutbox,
      curMailbox,
      this.isThreadMode === true,
    )

    return this.emailInfoMap[emailId]
  }

  extractInviteAddressList = (
    accountEmail: string,
    from: any[],
    to: any[],
    cc: any[],
  ): ReturnType<typeof extractInviteAddressListShared> => {
    return extractInviteAddressListShared(accountEmail, from, to, cc)
  }

  onCreateInvitation = (startDate: string, endDate: string, _subject: string): void => {
    onCreateInvitationShared(
      OutlookAvailabilityTime.outlookAvailabilityTime2Calendar,
      modalEventEditor as (
        start: unknown,
        end: unknown,
        subject: string,
        required: unknown,
        optional: unknown,
      ) => void,
      this.props,
      startDate,
      endDate,
    )
  }

  handleInvitationMenuClick = (key: string) => {
    const { invitationData } = this.state

    if (!invitationData) return

    switch (key) {
      case 'createEvent':
        this.handleCreateEvent(invitationData)
        break
      case 'showCalendar':
        this.handleShowCalendar(invitationData)
        break
      case 'copy':
        this.handleCopyInvitation(invitationData)
        break
      default:
        break
    }

    // Close menu after selection
    this.setState({ invitationMenuVisible: false })
  }

  handleCreateEvent = (invitationData: any): void => {
    handleCreateEventShared(
      modalEventEditor as (
        start: unknown,
        end: unknown,
        subject: string,
        required: unknown,
        optional: unknown,
      ) => void,
      invitationData,
    )
  }

  handleShowCalendar = (invitationData: any): void => {
    handleShowCalendarShared(moment, invitationData)
  }

  handleCopyInvitation = (invitationData: any): void => {
    handleCopyInvitationShared(omMessage, i18n.t.bind(i18n), invitationData)
  }

  handleInvitationMenuVisibleChange = (visible: boolean) => {
    this.setState({ invitationMenuVisible: visible })
  }

  onEmailChanged = (evt: any) => {
    const { email, emails } = this.props

    // 표시되고 있는 이메일이 없으면 종료.
    if (email == null && emails == null) {
      return
    }

    // flag, read 상태 변경 시 호출.
    if (evt.type === TYPE.EMAIL && (evt.action === ACTION.UPDATE || evt.action === ACTION.MODIFY)) {
      const emailList = emails != null ? emails : [email]

      emailList.forEach((item: any) => {
        let existItem = null

        const { messageId } = evt.data
        if (messageId != null && item.data.messageId != null) {
          const msgId = getMessageId(item.data.messageId[0])
          if (msgId === messageId) {
            existItem = item
          }
        }

        if (existItem == null) {
          existItem = evt.data.list.find((evtItem: any) => {
            return evtItem.data.id === item.data.id
          })
        }

        if (existItem != null) {
          const viewerWindow = this.viewerWindow
          if (viewerWindow != null && viewerWindow.webObj != null && this.emailInfoMap != null) {
            let emailInfo = this.emailInfoMap[existItem.getId()]
            let changedData: any = {}
            if (emailInfo == null) {
              emailInfo = this.createEmailInfo(existItem)
              changedData = emailInfo.dataForJSON
            } else if (emailInfo.dataForJSON.isLoaded === false && existItem.isLoaded === true) {
              emailInfo.generateData()
              changedData = emailInfo.dataForJSON
            } else {
              emailInfo.generateData()
              changedData = emailInfo.dataForJSON
              // const curData = emailInfo.dataForJSON
              // changedData = {
              //   summary: curData.summary,
              //   flagged: curData.flagged,
              //   draft: curData.draft,
              //   read: curData.read,
              //   received_time: curData.received_time,
              // }
            }

            const changedDataStr = JSON.stringify(changedData)

            let notiCnt = 0
            const notiCheck = () => {
              // M5 — InnerViewer unmount 후 재귀 setTimeout 이 stale viewerWindow.webObj
              // 호출 회피. inner mode 는 host window 의 timer 가 unmount 후에도 살아남음.
              if (this.mounted !== true) return
              const emailIds = viewerWindow.webObj.getEmailIds()
              if (emailIds.indexOf(existItem.getId()) < 0 && notiCnt < 20) {
                setTimeout(notiCheck, 100)
                notiCnt += 1
              } else {
                viewerWindow.webObj.notifyEmailChanged(existItem.data.id, changedDataStr)
              }
            }
            notiCheck()
          }
        }
      })
    }
  }

  applyThread = () => {
    if (this.applyThreadFunc != null) {
      this.applyThreadFunc()
    }
  }

  getJMAPMessageId = (email: any): string => {
    return getJMAPMessageIdShared(email)
  }

  applyDateTimeFormat = () => {
    const { dateFormat, timeFormat } = this.props

    const viewerWindow = this.viewerWindow
    if (viewerWindow != null && viewerWindow.webObj != null) {
      const settings = {
        dateFormat,
        timeFormat,
      }
      const settingsStr = JSON.stringify(settings)
      viewerWindow.webObj.setSettings(settingsStr, true)
    }
  }

  applyCurEmail = () => {
    const { curEmail, byViewer } = this.props

    console.log(' --- applyCurEmail : ', curEmail)

    const viewerWindow = this.viewerWindow
    if (viewerWindow != null && viewerWindow.webObj != null) {
      // const messageId = this.getJMAPMessageId(curEmail)
      if (curEmail != null) {
        const emailId = curEmail.data.id
        viewerWindow.webObj.select(emailId, false, byViewer)
      }
    }
  }

  // viewerExt 가 mailview.callOnReady 직후 호출 (viewerExt.tsx:291).
  // iframe 모드의 IFrameViewer 에서는 height 추적용 ResizeObserver 를 부착했지만
  // inner mode 는 container <div> 가 host CSS layout 에 자연 편입되어 별도 처리
  // 불필요. Phase 2c 에서 시각 회귀 발견 시 보강.
  onReady = () => {
    // noop — inner mode 에서는 viewer height 추적이 host layout 으로 자동 처리.
  }

  setContainer = (ref: HTMLDivElement | null) => {
    this.containerRef = ref
  }

  render() {
    const { showLoadingAni, isLoading, innerMounted, reloadKey } = this.state

    return (
      <div className={classNames(styles.threadView, 'threadView')}>
        <OmSpinner spinning={isLoading}>
          <div
            className={classNames(styles.mailBody, innerStyles.innerViewerContainer)}
            data-testid="mail-body"
            // Sentry Session Replay(rrweb)가 host 트리에 직접 마운트된 메일 본문 DOM까지
            // 재귀 직렬화하면서 오래 열어둘수록 로딩 지연이 폭증한다. 본문 영역을 replay 기록에서 제외한다.
            data-sentry-block
            ref={this.setContainer}
          >
            {innerMounted && <EmailContainer key={reloadKey} />}
          </div>
        </OmSpinner>
        <div className={classNames(styles.loadingAni, { show: showLoadingAni })}>
          <img src={syncAniGIF} alt="sync" />
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps)(InnerViewer)
